import { Box, Container } from '@mui/material'
import { LastEquipmentInteractionDto } from '@operations/app/api'
import { useOperationsStore } from '@operations/AppProvider'
import { OperationalEquipmentForm } from '@operations/features/operationalEquipment/components/organisms/OperationalEquipmentForm'
import { OperationalEquipmentFormHeader } from '@operations/features/operationalEquipment/components/organisms/OperationalEquipmentFormHeader'
import { useOperationEquipmentMessageHandler } from '@operations/features/operationalEquipment/hooks/useOperationEquipmentMessageHandler'
import { IOperationalEquipmentFormData } from '@operations/features/operationalEquipment/models/operational-equipment-assignment.model'
import { equipmentService } from '@operations/services'
import { useMinimalsTheme } from '@tom-ui/ui'
import { PATHS, useAsyncFetch, useFormStore, withAuthenticationRequired } from '@tom-ui/utils'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'

export const OperationalEquipmentFormPage = withAuthenticationRequired(
  observer(() => {
    const theme = useMinimalsTheme()
    const { ids } = useParams<{ ids: string }>()
    const navigate = useNavigate()
    const [lastInteraction, setLastInteraction] = useState<LastEquipmentInteractionDto>()
    const { appStore, operationalEquipmentStore } = useOperationsStore()
    const { getExceptionMessage, getSuccessMessage } = useOperationEquipmentMessageHandler()
    const formStore = useFormStore<IOperationalEquipmentFormData>()

    const formId = 'operationalEquipmentForm'

    const equipmentIds = useMemo(() => ids?.split(',').map(id => Number(id)) || [], [ids])

    useAsyncFetch(
      async () => {
        await operationalEquipmentStore.fetchDataByIds(equipmentIds)

        if (equipmentIds.length === 1) {
          const interaction = await equipmentService.getLastInteractionById(equipmentIds[0])
          setLastInteraction(interaction)
        }
      },
      [equipmentIds, operationalEquipmentStore],
      { onError: error => appStore.setShowAlert('error', getExceptionMessage(error)) },
    )

    const operationalEquipments = computed(() =>
      operationalEquipmentStore.items.filter(x => equipmentIds.includes(x.id)),
    ).get()

    const onSubmit = async () => {
      try {
        const data: IOperationalEquipmentFormData = {
          ...formStore.data,
          equipmentIds: operationalEquipments.map(x => x.id),
        }

        if (formStore.validate()) {
          await operationalEquipmentStore.updateEquipmentsAssignment(data)
          appStore.setShowAlert('success', getSuccessMessage())
          navigate(PATHS.controlRoom.operationalEquipment.url)
        }
      } catch (error) {
        appStore.setShowAlert('error', getExceptionMessage(error))
      }
    }

    return (
      <Box
        sx={{
          bgcolor: theme.palette.grey[100],
          minHeight: '100%',
        }}
      >
        <OperationalEquipmentFormHeader
          operationalEquipments={operationalEquipments}
          lastInteraction={lastInteraction}
          formStore={formStore}
          onSubmit={onSubmit}
        />

        <Container sx={{ paddingY: 2 }}>
          <OperationalEquipmentForm
            formId={formId}
            operationalEquipments={operationalEquipments}
            formStore={formStore}
          />
        </Container>
      </Box>
    )
  }),
)
